import type {
  AgentBillingPlan,
  AgentBillingSubscriptionInterval,
  CreditBalanceType,
  FeatureType,
  PriceInterval,
  StripeSubscriptionStatus,
  SubscriptionView,
} from "schemas/agent-billing";
import type { RequestFn } from "./request";

export type BillingPreferences = {
  topupEnabledAt: string | null;
  topupThreshold: number | null;
  topupRefillTarget: number | null;
  topupFailedAt: string | null;
  monthlySpendLimit: number | null;
};

export type BillingBalance = {
  id: string;
  balance: number | null;
  type: CreditBalanceType;
  interval: string;
  expiresAt: string | null;
};

export type BillingDailySpendPoint = {
  day: string;
  cents: number;
};

export type BillingUsageCycle = {
  cycleStart: string;
  cycleSpendCents: number;
  limitCents: number | null;
};

export type BillingTransaction = {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number | null;
  balanceType: string | null;
  stripeTransactionId: string | null;
  stripeHostedInvoiceUrl: string | null;
  createdAt: string;
  opencodeMessageId: string | null;
  opencodeMessage: {
    id: string;
    model: string | null;
    cost: number | null;
    tokensInput: number | null;
    tokensOutput: number | null;
    tokensReasoning: number | null;
    tokensCacheRead: number | null;
    tokensCacheWrite: number | null;
  } | null;
};

export type BillingSummary = {
  workspaceId: string;
  balances: BillingBalance[];
  preferences: BillingPreferences;
  dailySpend: BillingDailySpendPoint[];
  cycle: BillingUsageCycle;
  transactions: BillingTransaction[];
};

export type BillingPrice = {
  interval: PriceInterval;
  amount: number;
  currency: string;
  priceId: string;
  productId?: string;
  isDefault?: boolean;
  isSuperuser?: boolean;
  isDeprecated?: boolean;
};

export type BillingFeature = {
  featureType: FeatureType;
  units: number | null;
  minimumCapacity: number;
};

export type BillingProduct = {
  productType: string;
  featureType: FeatureType;
  displayName: string;
  units: number;
  isEligible: boolean;
  maxProductCount: number | null;
  currentProductCount: number;
  prices: BillingPrice[];
};

export type BillingRateCard = {
  rank: number | null;
  plan: AgentBillingPlan | null;
  canUpgrade: boolean;
  title: string;
  prices: BillingPrice[];
  features: BillingFeature[];
  addOnProducts: BillingProduct[];
};

export type BillingAgentSubscription = {
  id: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  status: StripeSubscriptionStatus;
  startedAt: Date;
  cancelAt: Date | null;
  currentPeriodEnd: Date | null;
  interval: AgentBillingSubscriptionInterval;
  plan: AgentBillingPlan;
  amount: number;
  currency: string;
  hasCustomPricing: boolean;
  items: {
    id: string;
    stripePriceId: string;
    stripeProductId: string;
    unitAmount: number;
    quantity: number;
    productType: string;
    currency: string;
  }[];
};

export type BillingUsage = {
  current: number;
  maximum: number | null;
  isFromEntitlement: boolean;
};

export type BillingCheckoutResult = {
  success: true;
  confirmationRequired: boolean;
  confirmationUrl: string | null;
};

type RawAgentSubscription = Omit<
  BillingAgentSubscription,
  "startedAt" | "cancelAt" | "currentPeriodEnd" | "hasCustomPricing"
> & {
  startedAt: string;
  cancelAt: string | null;
  currentPeriodEnd: string | null;
  // Optional so the SDK tolerates responses from a bedrock-api that hasn't deployed this field yet. (yuxin, 2026-07-14, REPL-29329)
  hasCustomPricing?: boolean;
};

function parseAgentSubscription(
  raw: RawAgentSubscription | null,
): BillingAgentSubscription | null {
  if (!raw) {
    return null;
  }
  return {
    ...raw,
    startedAt: new Date(raw.startedAt),
    cancelAt: raw.cancelAt ? new Date(raw.cancelAt) : null,
    currentPeriodEnd: raw.currentPeriodEnd
      ? new Date(raw.currentPeriodEnd)
      : null,
    hasCustomPricing: raw.hasCustomPricing ?? false,
  };
}

export function createBillingClient({ request }: { request: RequestFn }) {
  return {
    get: ({ workspaceId }: { workspaceId: string }) => {
      return request<BillingSummary>({
        method: "GET",
        path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing`,
      });
    },
    updatePreferences: ({
      workspaceId,
      ...input
    }: {
      workspaceId: string;
      preferences: Partial<BillingPreferences>;
    }) => {
      return request<BillingPreferences>({
        method: "PATCH",
        path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/preferences`,
        body: input,
      });
    },
    subscription: {
      find: async ({ workspaceId }: { workspaceId: string }) => {
        const { subscription } = await request<{
          subscription: RawAgentSubscription | null;
        }>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/subscription`,
        });
        return parseAgentSubscription(subscription);
      },
      findRateCards: async ({ workspaceId }: { workspaceId: string }) => {
        const { rateCards } = await request<{ rateCards: BillingRateCard[] }>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/rate-cards`,
        });
        return rateCards;
      },
    },
    credits: {
      find: async ({ workspaceId }: { workspaceId: string }) => {
        const { balances } = await request<{ balances: BillingBalance[] }>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/credits`,
        });
        return balances;
      },
      purchase: ({
        workspaceId,
        ...input
      }: {
        workspaceId: string;
        featureType: FeatureType;
        quantity: number;
      }) => {
        return request<{ success: true }>({
          method: "POST",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/credits/purchase`,
          body: input,
        });
      },
    },
    // Superuser-only debug affordance (server re-checks): drains the credit balance to `targetBalance` (default 0).
    debugDrainCredits: ({
      workspaceId,
      targetBalance,
    }: {
      workspaceId: string;
      targetBalance?: number;
    }) => {
      return request<{ balance: number | null }>({
        method: "POST",
        path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/debug/drain-credits`,
        body: { targetBalance: targetBalance ?? 0 },
      });
    },
    checkout: {
      createUpdateSubscriptionSession: ({
        workspaceId,
        ...input
      }: {
        workspaceId: string;
        subscriptionView: SubscriptionView;
        successUrl: string;
        cancelUrl?: string;
      }) => {
        return request<BillingCheckoutResult>({
          method: "POST",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/checkout/session`,
          body: input,
        });
      },
      cancel: ({ workspaceId }: { workspaceId: string }) => {
        return request<{ success: true }>({
          method: "POST",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/checkout/cancel`,
        });
      },
    },
    workspaceUsage: {
      getReploSites: ({ workspaceId }: { workspaceId: string }) => {
        return request<BillingUsage>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/usage/replo-sites`,
        });
      },
      getShopifyThemeProjects: ({ workspaceId }: { workspaceId: string }) => {
        return request<BillingUsage>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/usage/shopify-theme-projects`,
        });
      },
      getPublishedElements: ({ workspaceId }: { workspaceId: string }) => {
        return request<BillingUsage>({
          method: "GET",
          path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/usage/published-elements`,
        });
      },
    },
    getStripePortalUrl: async ({
      workspaceId,
      returnUrl,
    }: {
      workspaceId: string;
      returnUrl: string;
    }) => {
      const { url } = await request<{ url: string }>({
        method: "GET",
        path: `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/stripe-portal-url`,
        query: { returnUrl },
      });
      return url;
    },
  };
}
